import { useState, useEffect, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { useClientCategories } from '../lib/useClientCategories';
import { Input, Select, SearchableSelect, Button } from '../atoms';
import { Wizard, useToast, AddressAutocomplete, CreateEntityModal, PropertyScanIntake, type ScannedDocument } from '../organisms';
import { groupSuggestions, bestSource, LOW_CONFIDENCE, type Suggestion, type WizardField, type FieldSuggestion } from '../lib/scanSuggestions';
import { STATE_SELECT_OPTIONS } from '../lib/us-states';
import { useCurrentUser } from '../lib/useCurrentUser';

/**
 * PropertySetupWizard — /v2/properties/new
 *
 * 4-step wizard that creates property + first parcel + jurisdictions +
 * tax records + invoice term across 5 separate POSTs (matches v1
 * submitPropertyWizard sequence — there is no single endpoint).
 *
 * Step 1: Property info — client + name + address
 * Step 2: Details — RE/PP + type + dimensions
 * Step 3: Parcel & Assessor — state-cascade assessor + jurisdictions
 * Step 4: Commission & Billing — agent + rate or flat fee
 *
 * Optional ?client_id= query param pre-selects the client (e.g., when
 * launched from a Client detail page).
 *
 * Phase 11 Block 8 PR 6 (2026-05-12).
 */

type RePp = 'real_estate' | 'personal_property' | 'real_and_personal';
type BillingType = 'contingency' | 'flat_fee';

interface State {
  client_id: string;
  name: string;
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  re_pp: RePp;
  prop_type: string;
  // Spec 0011: one Category from the firm's list (the parcel starts with it) and the
  // property's group label.
  category: string;
  group_label: string;
  land_sf: string;
  gba: string;
  nla: string;
  units: string;
  unit_type: string;
  year_built: string;
  assessor_id: string;
  primary_collector_id: string;
  account_number: string;
  alternate_id: string;
  owner_name: string;
  legal_description: string;
  jurisdiction_ids: string[];
  commission_agent_user_id: string;
  consultant_user_id: string;   // ATA part 3 §5c: Consultant slot 1, written to property_assignments
  // Scan-documents PRD: acquisition and income fields a deed or an I&E workbook can prefill.
  legal_owner_name: string;
  purchase_price: string;
  purchase_date: string;
  reported_noi: string;
  reported_noi_year: string;
  billing_type: BillingType;
  rate: string;
  flat_amount: string;
  terms: string;
}

const EMPTY: State = {
  client_id: '', name: '', address_line1: '', city: '', state: 'TX', postal_code: '',
  re_pp: 'real_estate', prop_type: '', category: '', group_label: '', land_sf: '', gba: '', nla: '', units: '', unit_type: '', year_built: '',
  assessor_id: '', primary_collector_id: '',
  account_number: '', alternate_id: '', owner_name: '', legal_description: '',
  jurisdiction_ids: [],
  commission_agent_user_id: '',
  consultant_user_id: '',
  legal_owner_name: '', purchase_price: '', purchase_date: '', reported_noi: '', reported_noi_year: '',
  billing_type: 'contingency', rate: '', flat_amount: '', terms: 'Net 30',
};
interface ExistingTerm { id: string; name: string | null; billing_type: string | null }

interface Client { id: string; name: string; assigned_agent_user_id: string | null; }
interface Assessor { id: string; name: string; }
interface Collector { id: string; name: string; }
interface Jurisdiction { id: string; name: string; }
interface User { id: string; first_name: string | null; last_name: string | null; email: string; role: string; }

export function PropertySetupWizard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const preClientId = params.get('client_id') || '';
  const [state, setState] = useState<State>(() => ({ ...EMPTY, client_id: preClientId }));
  const [submitting, setSubmitting] = useState(false);
  // Scan-documents PRD: what the uploaded documents suggest, what the admin decided, and which
  // fields they typed themselves this session (a suggestion never overwrites those).
  const [scanDocs, setScanDocs] = useState<ScannedDocument[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [decided, setDecided] = useState<Record<string, 'accepted' | 'dismissed'>>({});
  const [typed, setTyped] = useState<Set<WizardField>>(new Set());
  const grouped = groupSuggestions(suggestions);
  /** The pending suggestion for a field: none once decided or once the admin typed the field. */
  const pending = (field: WizardField): FieldSuggestion | null => {
    const g = grouped.get(field);
    if (!g || decided[field] || typed.has(field)) return null;
    return g;
  };
  const unresolvedConflicts = [...grouped.values()].filter((g) => g.conflict && !decided[g.field]);
  const accept = (field: WizardField, value: string, documentId: string) => {
    set(field)(value as never);
    setDecided((d) => ({ ...d, [field]: 'accepted' }));
    setAcceptedFrom((a) => ({ ...a, [field]: { document_id: documentId, value } }));
  };
  const dismiss = (field: WizardField) => setDecided((d) => ({ ...d, [field]: 'dismissed' }));
  const acceptAll = (fields: WizardField[]) => {
    for (const f of fields) {
      const g = pending(f);
      if (g && !g.conflict) { const src = bestSource(g.choices[0]); accept(f, g.choices[0].value, src.documentId); }
    }
  };
  const [acceptedFrom, setAcceptedFrom] = useState<Record<string, { document_id: string; value: string }>>({});
  const setTyped_ = <K extends keyof State>(k: K) => (v: State[K]) => { setTyped((t) => new Set(t).add(k as WizardField)); set(k)(v); };

  const clientsQuery = useQuery<Client[]>({
    queryKey: ['clients-list'],
    queryFn: ({ signal }) => api.get<Client[]>('/api/clients', signal),
  });

  const usersQuery = useQuery<User[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get<User[]>('/api/users-list', signal),
  });

  // C1 / C2 — Property Type + Unit Type are org-configurable dropdowns (Part E).
  const clientCategories = useClientCategories();
  const propertyTypesQuery = useQuery<string[]>({ queryKey: ['org-settings', 'property-types'], queryFn: ({ signal }) => api.get<string[]>('/api/org-settings/property-types', signal), staleTime: 5 * 60 * 1000 });
  const unitTypesQuery = useQuery<string[]>({ queryKey: ['org-settings', 'unit-types'], queryFn: ({ signal }) => api.get<string[]>('/api/org-settings/unit-types', signal), staleTime: 5 * 60 * 1000 });

  // Cascade: assessors filtered by state
  const assessorsQuery = useQuery<Assessor[]>({
    queryKey: ['assessors-by-state', state.state],
    queryFn: ({ signal }) => api.get<Assessor[]>(`/api/assessors-by-state/${state.state}`, signal),
    enabled: !!state.state,
  });

  const collectorsQuery = useQuery<Collector[]>({
    queryKey: ['collectors-by-assessor', state.assessor_id],
    queryFn: ({ signal }) => api.get<Collector[]>(`/api/collectors-by-assessor/${state.assessor_id}`, signal),
    enabled: !!state.assessor_id,
  });

  const jurisdictionsQuery = useQuery<Jurisdiction[]>({
    queryKey: ['jurisdictions-by-assessor', state.assessor_id],
    queryFn: ({ signal }) => api.get<Jurisdiction[]>(`/api/jurisdictions-by-assessor/${state.assessor_id}`, signal),
    enabled: !!state.assessor_id,
  });

  // Clear assessor_id when state changes (cascade invalidates downstream)
  useEffect(() => {
    setState(s => ({ ...s, assessor_id: '', primary_collector_id: '', jurisdiction_ids: [] }));
  }, [state.state]);

  // Default commission agent to the client's assigned manager when client lands
  useEffect(() => {
    if (state.client_id && !state.commission_agent_user_id && clientsQuery.data) {
      const client = clientsQuery.data.find(c => c.id === state.client_id);
      if (client?.assigned_agent_user_id) {
        setState(s => ({ ...s, commission_agent_user_id: client.assigned_agent_user_id! }));
      }
    }
  }, [state.client_id, state.commission_agent_user_id, clientsQuery.data]);

  const set = <K extends keyof State>(k: K) => (v: State[K]) => setState(s => ({ ...s, [k]: v }));

  // Filter on role only: /api/users-list already serves active accounts only.
  const agentUsers = (usersQuery.data ?? []).filter(u => u.role === 'agent');
  const staffUsers = usersQuery.data ?? [];

  // ATA part 3 §5c: when the firm has billing fields block save, the last step needs Consultant
  // slot 1 and a fee term for the year (one already on file, or the rate / flat fee entered here).
  const requiredQuery = useQuery<{ billing_required_fields?: { enabled?: boolean } }>({
    queryKey: ['org-settings', 'billing-required-fields'],
    queryFn: ({ signal }) => api.get('/api/org-settings/billing-required-fields', signal),
    retry: false,
  });
  const billingRequired = requiredQuery.data?.billing_required_fields?.enabled === true;
  const year = new Date().getFullYear();
  const termsQuery = useQuery<ExistingTerm[]>({
    queryKey: ['invoice-terms', 'client', state.client_id, year],
    queryFn: ({ signal }) => api.get<ExistingTerm[]>(`/api/invoice-terms?client_id=${encodeURIComponent(state.client_id)}&year=${year}`, signal),
    enabled: !!state.client_id,
    retry: false,
  });
  const existingTerm = Array.isArray(termsQuery.data) && termsQuery.data.length > 0 ? termsQuery.data[0] : null;
  const termEntered = (state.billing_type === 'contingency' && state.rate.trim() !== '') || (state.billing_type === 'flat_fee' && state.flat_amount.trim() !== '');

  // C4: Commission & billing (step 4) is set only by a super_admin or an office
  // administrator — other roles create the property without the billing term.
  const currentUser = useCurrentUser();
  const canSetBilling = currentUser?.role === 'super_admin' || currentUser?.role === 'administrator';
  // C3: inline "add assessor" escape when the chosen state has none on file.
  const [addAssessorOpen, setAddAssessorOpen] = useState(false);

  async function submitWizard() {
    setSubmitting(true);
    try {
      // 1. Create property
      const propData = {
        client_id: state.client_id, name: state.name,
        address_line1: state.address_line1 || null,
        city: state.city || null,
        state: state.state || null,
        postal_code: state.postal_code || null,
        prop_type: state.prop_type || null,
        category: state.category || null,
        group_label: state.group_label.trim() || null,
        re_pp: state.re_pp,
        land_sf: state.land_sf ? Number(state.land_sf) : null,
        gba: state.gba ? Number(state.gba) : null,
        nla: state.nla ? Number(state.nla) : null,
        units: state.units ? Number(state.units) : null,
        unit_type: state.unit_type || null,
        year_built: state.year_built ? Number(state.year_built) : null,
        // Scan-documents PRD: acquisition and income fields, when a deed or an I&E workbook filled them.
        legal_owner_name: state.legal_owner_name || null,
        purchase_price: state.purchase_price ? Number(state.purchase_price) : null,
        purchase_date: state.purchase_date || null,
        reported_noi: state.reported_noi ? Number(state.reported_noi) : null,
        reported_noi_year: state.reported_noi_year ? Number(state.reported_noi_year) : null,
        // C4 — only a super_admin / office administrator sets commission (adversarial
        // -review finding #7: the auto-default set it for every role). A non-privileged
        // creator posts no commission agent; an admin assigns it from Billing later.
        commission_agent_user_id: canSetBilling ? (state.commission_agent_user_id || null) : null,
        year: new Date().getFullYear(),
        status: 'active',
      };
      // 0. Fee term first when none is on file for the year: with billing fields blocking save, the
      //    property needs its client to carry a term before it can be created.
      const hasRate = state.billing_type === 'contingency' && state.rate;
      const hasFlat = state.billing_type === 'flat_fee' && state.flat_amount;
      if ((hasRate || hasFlat) && !existingTerm) {
        await api.post('/api/invoice-terms', {
          client_id: state.client_id,
          year: new Date().getFullYear(),
          name: `${state.name} ${state.billing_type}`,
          billing_type: state.billing_type,
          rate: hasRate ? Number(state.rate) / 100 : null,
          flat_amount: hasFlat ? Number(state.flat_amount) : null,
          terms: state.terms || 'Net 30',
          terms_days: 30,
        });
      }

      const prop = await api.post<{ id: string }>('/api/properties', propData);

      // 1b. Consultant slot 1 (ATA part 3 §2/§5c) lives on the property's assignments.
      if (canSetBilling && state.consultant_user_id) {
        await api.put(`/api/property-assignments/${prop.id}`, { roles: { consultant: [{ slot: 1, user_id: state.consultant_user_id }] } });
      }

      // 2. Create first parcel
      const parcelData = {
        property_id: prop.id,
        assessor_id: state.assessor_id,
        primary_collector_id: state.primary_collector_id || null,
        legal_description: state.legal_description || null,
        account_number: state.account_number,
        alternate_id: state.alternate_id || null,
        owner_name: state.owner_name || null,
        name: state.name,
        re_pp: state.re_pp === 'real_and_personal' ? 'real_estate' : state.re_pp,
        state: state.state || null,
        land_sf: state.land_sf ? Number(state.land_sf) : null,
        gba: state.gba ? Number(state.gba) : null,
        nla: state.nla ? Number(state.nla) : null,
        units: state.units ? Number(state.units) : null,
        unit_type: state.unit_type || null,
        year: new Date().getFullYear(),
        status: 'active',
      };
      const parcel = await api.post<{ id: string }>('/api/parcels', parcelData);

      // 2b. Scan-documents PRD: attach the scanned documents to the new property, log which
      //     fields came from which document and who accepted them, then re-read each stored
      //     reading (free, no model call) so its values stage for review against the new parcel.
      const attachedIds = scanDocs.filter((d) => d.phase !== 'failed' || !d.id.startsWith('failed:')).map((d) => d.id).filter((id) => !id.startsWith('failed:'));
      if (attachedIds.length) {
        try {
          await api.post('/api/document-extract/prefill-accepted', {
            property_id: prop.id, document_ids: attachedIds,
            accepted: Object.entries(acceptedFrom).map(([field, a]) => ({ field, document_id: a.document_id, value: a.value })),
          });
        } catch { /* the property exists; attaching is best-effort and visible in Documents */ }
        for (const d of scanDocs) {
          if (d.phase === 'ready' && d.docType && d.docType !== 'Operating Statement' && !d.id.startsWith('failed:')) {
            try { await api.post(`/api/document-extract/document/${d.id}`, { reuse_reading: true }); } catch { /* review staging is a bonus */ }
          }
        }
      }

      // 3-4. Link jurisdictions + auto-create tax records (idempotent bulk endpoints)
      if (state.jurisdiction_ids.length) {
        await api.post('/api/property-jurisdiction-list/bulk', {
          property_id: prop.id,
          jurisdiction_ids: state.jurisdiction_ids,
        });
        await api.post('/api/tax-records/bulk', {
          parcel_id: parcel.id,
          jurisdiction_ids: state.jurisdiction_ids,
          tax_year: new Date().getFullYear(),
        });
      }

      toast({ variant: 'success', message: `Property "${state.name}" created.` });
      navigate(`/properties/${prop.id}`);
    } catch (err) {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : (err as Error).message;
      toast({ variant: 'error', message: msg });
      setSubmitting(false);
    }
  }

  const canAdvance = (step: number): boolean => {
    if (step === 0) {
      // State lives on THIS (Info) step + drives the assessor cascade, so require it
      // here — checking it on the next step dead-ended the wizard with the field a
      // step back (adversarial-review finding #6).
      return state.client_id !== '' && state.name.trim().length > 0 && state.state.length === 2;
    }
    if (step === 1) {
      return true;
    }
    if (step === 2) {
      return state.assessor_id !== '' && state.account_number.trim().length > 0;
    }
    if (step === 3) {
      // Scan-documents PRD 2a.5: a field two documents disagree on blocks Save until one is chosen.
      if (unresolvedConflicts.length) return false;
      if (billingRequired && canSetBilling) return state.consultant_user_id !== '' && (!!existingTerm || termEntered);
    }
    return true;
  };

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>New Property Setup</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          4 steps. Creates property + first parcel + jurisdictional tax records + billing term.
        </p>
      </header>

      <div style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-6)',
      }}>
        <Wizard
          steps={[
            { label: 'Property info', description: 'Client + address' },
            { label: 'Details', description: 'Type + dimensions' },
            { label: 'Parcel & Assessor', description: 'First parcel' },
            { label: 'Commission & Billing', description: 'Review + create' },
          ]}
          onSubmit={submitWizard}
          onCancel={() => navigate('/properties')}
          submitting={submitting}
          submitLabel="Create property"
          canAdvance={canAdvance}
        >
          {({ currentStep }) => (
            <>
              {currentStep === 0 && (
                <>
                  <PropertyScanIntake
                    clientId={state.client_id}
                    documents={scanDocs}
                    onDocuments={setScanDocs}
                    onSuggestions={setSuggestions}
                    disabled={submitting}
                  />
                  <SuggestionToolbar fields={['name', 'address_line1', 'city', 'state', 'postal_code']} pending={pending} onAcceptAll={acceptAll} />
                  <StepInfo
                    state={state}
                    set={setTyped_}
                    clients={clientsQuery.data ?? []}
                    clientLocked={!!preClientId}
                    suggest={(f) => <SuggestionBar field={f} pending={pending(f)} onAccept={accept} onDismiss={dismiss} />}
                  />
                </>
              )}
              {currentStep === 1 && (
                <>
                  <SuggestionToolbar fields={['prop_type', 'units', 'legal_owner_name', 'purchase_price', 'purchase_date', 'reported_noi', 'reported_noi_year']} pending={pending} onAcceptAll={acceptAll} />
                  <StepDetails state={state} set={setTyped_} propertyTypes={propertyTypesQuery.data ?? []} unitTypes={unitTypesQuery.data ?? []} categories={clientCategories.values}
                    suggest={(f) => <SuggestionBar field={f} pending={pending(f)} onAccept={accept} onDismiss={dismiss} />} />
                </>
              )}
              {currentStep === 2 && (
                <StepParcel
                  suggest={(f) => <SuggestionBar field={f} pending={pending(f)} onAccept={accept} onDismiss={dismiss} />}
                  state={state}
                  set={setTyped_}
                  assessors={assessorsQuery.data ?? []}
                  collectors={collectorsQuery.data ?? []}
                  jurisdictions={jurisdictionsQuery.data ?? []}
                  assessorsLoading={assessorsQuery.isLoading}
                  onAddAssessor={() => setAddAssessorOpen(true)}
                />
              )}
              {currentStep === 3 && (
                <StepCommission state={state} set={set} agentUsers={agentUsers} staffUsers={staffUsers} canSetBilling={canSetBilling} billingRequired={billingRequired} existingTerm={existingTerm} termEntered={termEntered} />
              )}
            </>
          )}
        </Wizard>
      </div>

      {/* C3 — create an assessor without leaving the wizard when the state has none
          (or to add a new one). On success the new assessor is auto-selected and the
          state-scoped assessor list refetches so it appears in the dropdown. */}
      <CreateEntityModal
        open={addAssessorOpen}
        onClose={() => setAddAssessorOpen(false)}
        table="assessors"
        routePath="assessors"
        defaultValues={{ state: state.state }}
        invalidateKeys={[['assessors-by-state', state.state]]}
        onSuccess={(rec) => {
          set('assessor_id')(rec.id);
          set('primary_collector_id')('');
          set('jurisdiction_ids')([]);
          setAddAssessorOpen(false);
        }}
      />
    </>
  );
}

type Suggest = (field: WizardField) => ReactNode;

function StepInfo({ state, set, clients, clientLocked, suggest }: {
  state: State; set: <K extends keyof State>(k: K) => (v: State[K]) => void;
  clients: Client[]; clientLocked: boolean; suggest?: Suggest;
}) {
  return (
    <Grid>
      <Field label="Client *" required>
        {/* C5 / B3 — searchable, alphabetical client picker (shared SearchableSelect).
            When pre-selected from a client page (?client_id=) it stays locked. */}
        {clientLocked ? (
          <Select
            value={state.client_id}
            onChange={e => set('client_id')(e.target.value)}
            disabled
            options={clients.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Select client…"
          />
        ) : (
          <SearchableSelect
            options={[...clients].sort((a, b) => a.name.localeCompare(b.name)).map(c => ({ value: c.id, label: c.name }))}
            value={state.client_id || null}
            onChange={(v) => set('client_id')(v ?? '')}
            placeholder="Search clients…"
          />
        )}
      </Field>
      <Field label="Property Name *" required>
        <Input value={state.name} onChange={e => set('name')(e.target.value)} placeholder="e.g. Alannah at Westover Hills" required />
        {suggest?.('name')}
      </Field>
      <Field label="Group Label">
        {/* Spec 0011: the group or region this property is filed under (one per property). */}
        <Input value={state.group_label} onChange={e => set('group_label')(e.target.value)} placeholder="e.g. Retail Group 3" />
      </Field>
      <Field label="Address" fullWidth>
        <AddressAutocomplete
          value={state.address_line1}
          onChange={set('address_line1')}
          onSelect={(a) => {
            // Fill Address + City + State + Postal Code from the picked suggestion;
            // each functional update composes so all four land. Manual entry still
            // works (the field degrades to a plain input when Smarty isn't keyed).
            set('address_line1')(a.line1);
            set('city')(a.city);
            set('state')(a.state);
            set('postal_code')(a.zip);
          }}
          placeholder="12105 State Hwy. 151"
        />
        {suggest?.('address_line1')}
      </Field>
      <Field label="City">
        <Input value={state.city} onChange={e => set('city')(e.target.value)} placeholder="San Antonio" />
        {suggest?.('city')}
      </Field>
      <Field label="State">
        {/* RH§3 — state is a dropdown of the canonical US codes, not free text. */}
        <Select value={state.state} onChange={e => set('state')(e.target.value)} options={STATE_SELECT_OPTIONS} />
        {suggest?.('state')}
      </Field>
      <Field label="Postal Code">
        <Input value={state.postal_code} onChange={e => set('postal_code')(e.target.value)} placeholder="78251" />
        {suggest?.('postal_code')}
      </Field>
    </Grid>
  );
}

function StepDetails({ state, set, propertyTypes, unitTypes, categories, suggest }: {
  state: State; set: <K extends keyof State>(k: K) => (v: State[K]) => void;
  propertyTypes: string[]; unitTypes: string[]; categories: string[]; suggest?: Suggest;
}) {
  const orgHint = <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>Don&rsquo;t see it? Add it in Org Settings.</span>;
  return (
    <Grid>
      <Field label="RE/PP *" required>
        <Select
          value={state.re_pp}
          onChange={e => set('re_pp')(e.target.value as RePp)}
          options={[
            { value: 'real_estate', label: 'Real estate' },
            { value: 'personal_property', label: 'Personal property' },
            { value: 'real_and_personal', label: 'Real + Personal' },
          ]}
        />
      </Field>
      <Field label="Property Type">
        {/* C1 — configurable dropdown (org-settings property-types). */}
        <Select value={state.prop_type} onChange={e => set('prop_type')(e.target.value)}
          options={propertyTypes.map(t => ({ value: t, label: t }))} placeholder="Select a type…" />
        {orgHint}
        {suggest?.('prop_type')}
      </Field>
      <Field label="Category">
        {/* Spec 0011: the same list the client's categories use (org-settings client-categories). */}
        <Select value={state.category} onChange={e => set('category')(e.target.value)}
          options={categories.map(c => ({ value: c, label: c }))} placeholder="Select a category…" />
        {orgHint}
      </Field>
      <Field label="Land (sqft)">
        <Input type="number" value={state.land_sf} onChange={e => set('land_sf')(e.target.value)} />
      </Field>
      <Field label="GBA (sqft)">
        <Input type="number" value={state.gba} onChange={e => set('gba')(e.target.value)} />
      </Field>
      <Field label="NLA (sqft)">
        <Input type="number" value={state.nla} onChange={e => set('nla')(e.target.value)} />
      </Field>
      <Field label="Units">
        <Input type="number" value={state.units} onChange={e => set('units')(e.target.value)} />
        {suggest?.('units')}
      </Field>
      <Field label="Unit type">
        {/* C2 — configurable dropdown (org-settings unit-types). */}
        <Select value={state.unit_type} onChange={e => set('unit_type')(e.target.value)}
          options={unitTypes.map(t => ({ value: t, label: t }))} placeholder="Select a unit type…" />
        {orgHint}
      </Field>
      <Field label="Year built">
        <Input type="number" value={state.year_built} onChange={e => set('year_built')(e.target.value)} />
      </Field>
      {/* Scan-documents PRD: acquisition and income facts a deed or an I&E workbook supplies. */}
      <Field label="Legal owner">
        <Input value={state.legal_owner_name} onChange={e => set('legal_owner_name')(e.target.value)} placeholder="Owner of record" />
        {suggest?.('legal_owner_name')}
      </Field>
      <Field label="Purchase price ($)">
        <Input type="number" value={state.purchase_price} onChange={e => set('purchase_price')(e.target.value)} />
        {suggest?.('purchase_price')}
      </Field>
      <Field label="Purchase date">
        <Input type="date" value={state.purchase_date} onChange={e => set('purchase_date')(e.target.value)} />
        {suggest?.('purchase_date')}
      </Field>
      <Field label="Reported NOI ($)">
        <Input type="number" value={state.reported_noi} onChange={e => set('reported_noi')(e.target.value)} />
        {suggest?.('reported_noi')}
      </Field>
      <Field label="NOI year">
        <Input type="number" value={state.reported_noi_year} onChange={e => set('reported_noi_year')(e.target.value)} placeholder="2025" />
        {suggest?.('reported_noi_year')}
      </Field>
    </Grid>
  );
}

function StepParcel({
  state, set, assessors, collectors, jurisdictions, assessorsLoading, onAddAssessor, suggest,
}: {
  state: State; set: <K extends keyof State>(k: K) => (v: State[K]) => void;
  assessors: Assessor[]; collectors: Collector[]; jurisdictions: Jurisdiction[];
  assessorsLoading: boolean; onAddAssessor: () => void; suggest?: Suggest;
}) {
  const noAssessors = !assessorsLoading && assessors.length === 0;
  return (
    <Grid>
      <Field label="Assessor *" required>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <Select
            value={state.assessor_id}
            onChange={e => {
              set('assessor_id')(e.target.value);
              set('primary_collector_id')('');
              set('jurisdiction_ids')([]);
            }}
            options={assessors.map(a => ({ value: a.id, label: a.name }))}
            placeholder={assessorsLoading ? 'Loading…' : noAssessors ? `No assessors in ${state.state || 'this state'}` : 'Select assessor…'}
            disabled={assessorsLoading || assessors.length === 0}
          />
          {/* C3 — escape hatch: create an assessor inline so a missing one never
              dead-ends the wizard. Prominent when the state has none; a convenience
              otherwise. Needs a state chosen (step 1) to scope the new assessor. */}
          {state.state && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              {noAssessors && (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-status-amber)' }}>
                  None on file for {state.state} —
                </span>
              )}
              <Button variant="secondary" size="sm" onClick={onAddAssessor}>+ New assessor</Button>
            </div>
          )}
        </div>
      </Field>
      <Field label="Account # *" required>
        <Input value={state.account_number} onChange={e => set('account_number')(e.target.value)} placeholder="1005301" required />
        {suggest?.('account_number')}
      </Field>
      <Field label="Alternate ID">
        <Input value={state.alternate_id} onChange={e => set('alternate_id')(e.target.value)} />
      </Field>
      <Field label="Owner name">
        <Input value={state.owner_name} onChange={e => set('owner_name')(e.target.value)} />
        {suggest?.('owner_name')}
      </Field>
      <Field label="Primary collector">
        <Select
          value={state.primary_collector_id}
          onChange={e => set('primary_collector_id')(e.target.value)}
          options={collectors.map(c => ({ value: c.id, label: c.name }))}
          placeholder={!state.assessor_id ? 'Select assessor first' : collectors.length === 0 ? 'No collectors on this assessor' : '— None —'}
          disabled={!state.assessor_id || collectors.length === 0}
        />
      </Field>
      <Field label="Legal description" fullWidth>
        <Input value={state.legal_description} onChange={e => set('legal_description')(e.target.value)} />
        {suggest?.('legal_description')}
      </Field>
      <Field label="Jurisdictions (parcel inherits)" fullWidth>
        <JurisdictionMultiSelect
          available={jurisdictions}
          selected={state.jurisdiction_ids}
          onChange={set('jurisdiction_ids')}
          disabled={!state.assessor_id}
        />
      </Field>
    </Grid>
  );
}

function StepCommission({
  state, set, agentUsers, staffUsers, canSetBilling, billingRequired, existingTerm, termEntered,
}: {
  state: State; set: <K extends keyof State>(k: K) => (v: State[K]) => void;
  agentUsers: User[]; staffUsers: User[]; canSetBilling: boolean;
  billingRequired: boolean; existingTerm: ExistingTerm | null; termEntered: boolean;
}) {
  const consultantMissing = billingRequired && state.consultant_user_id === '';
  const termMissing = billingRequired && !existingTerm && !termEntered;
  // C4 — only super_admin / office administrator set commission + billing. Other
  // roles review + create the property; the billing term is left to an admin.
  if (!canSetBilling) {
    return (
      <div style={{
        padding: 'var(--sp-4) var(--sp-5)',
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-ink-3)',
        fontSize: 'var(--fs-sm)',
        lineHeight: 'var(--lh-normal)',
      }}>
        <p style={{ margin: '0 0 var(--sp-2)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          Commission &amp; billing is set by an administrator
        </p>
        <p style={{ margin: 0 }}>
          You can finish creating the property, its first parcel, and its jurisdictional
          tax records now. A super admin or office administrator will add the commission
          agent and billing term afterward from the client&rsquo;s Billing tab.
        </p>
      </div>
    );
  }
  return (
    <Grid>
      {/* ATA part 3 §5c: Consultant slot 1 is billing-critical; slot 2 is set later on the property. */}
      <Field label="Consultant (slot 1)" required={billingRequired} error={consultantMissing ? 'Required before this property can be invoiced' : undefined}>
        <Select
          value={state.consultant_user_id}
          onChange={e => set('consultant_user_id')(e.target.value)}
          placeholder="— None —"
          invalid={consultantMissing}
          options={staffUsers.map(u => ({ value: u.id, label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email }))}
        />
      </Field>
      {existingTerm && (
        <Field label="Fee term" fullWidth>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
            On file for this year: <strong>{existingTerm.name || existingTerm.billing_type || 'fee term'}</strong>. Leave the rate and flat fee blank to use it.
          </div>
        </Field>
      )}
      {termMissing && (
        <Field label="" fullWidth error="A fee term is required before this property can be invoiced: enter a rate or a flat fee">
          <span />
        </Field>
      )}
      <Field label="Commission Agent">
        <Select
          value={state.commission_agent_user_id}
          onChange={e => set('commission_agent_user_id')(e.target.value)}
          placeholder="— None —"
          options={agentUsers.map(u => ({
            value: u.id,
            label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
          }))}
        />
      </Field>
      <Field label="Billing type">
        <Select
          value={state.billing_type}
          onChange={e => set('billing_type')(e.target.value as BillingType)}
          options={[
            { value: 'contingency', label: 'Contingency (%)' },
            { value: 'flat_fee', label: 'Flat fee ($)' },
          ]}
        />
      </Field>
      {state.billing_type === 'contingency' && (
        <Field label="Rate (%)">
          <Input type="number" value={state.rate} onChange={e => set('rate')(e.target.value)} placeholder="e.g. 30" />
        </Field>
      )}
      {state.billing_type === 'flat_fee' && (
        <Field label="Flat fee amount ($)">
          <Input type="number" value={state.flat_amount} onChange={e => set('flat_amount')(e.target.value)} placeholder="e.g. 5000" />
        </Field>
      )}
      <Field label="Payment terms">
        <Input value={state.terms} onChange={e => set('terms')(e.target.value)} placeholder="Net 30" />
      </Field>
    </Grid>
  );
}

function JurisdictionMultiSelect({
  available, selected, onChange, disabled,
}: {
  available: Jurisdiction[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
}) {
  if (disabled) {
    return (
      <div style={{
        padding: 'var(--sp-2) var(--sp-3)',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-md)',
        color: 'var(--color-muted)',
        fontSize: 'var(--fs-sm)',
      }}>Select an assessor first.</div>
    );
  }
  if (available.length === 0) {
    return (
      <div style={{
        padding: 'var(--sp-2) var(--sp-3)',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-md)',
        color: 'var(--color-muted)',
        fontSize: 'var(--fs-sm)',
      }}>No jurisdictions associated with this assessor.</div>
    );
  }
  return (
    <div style={{
      maxHeight: 160,
      overflowY: 'auto',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-md)',
      background: 'var(--color-card)',
      padding: 'var(--sp-2)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--sp-1)',
    }}>
      {available.map(j => {
        const checked = selected.includes(j.id);
        return (
          <label key={j.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            padding: 'var(--sp-1) var(--sp-2)',
            cursor: 'pointer',
            fontSize: 'var(--fs-sm)',
            borderRadius: 'var(--r-sm)',
            background: checked ? 'var(--brand-primary-bg)' : 'transparent',
          }}>
            <input
              type="checkbox"
              checked={checked}
              style={{ accentColor: 'var(--brand-primary)' }}
              onChange={e => {
                if (e.target.checked) {
                  onChange([...selected, j.id]);
                } else {
                  onChange(selected.filter(id => id !== j.id));
                }
              }}
            />
            <span>{j.name}</span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Scan-documents PRD 2a.3 to 2a.5: a suggested value shown under its field with the document it
 * came from, Accept and Dismiss; a low-confidence reading carries a caution marker; a field two
 * documents disagree on shows both values and forces a choice.
 */
function SuggestionBar({ field, pending, onAccept, onDismiss }: {
  field: WizardField; pending: FieldSuggestion | null;
  onAccept: (field: WizardField, value: string, documentId: string) => void; onDismiss: (field: WizardField) => void;
}) {
  if (!pending) return null;
  const box: React.CSSProperties = {
    marginTop: 'var(--sp-1)', padding: 'var(--sp-2) var(--sp-3)', borderRadius: 'var(--r-md)',
    background: pending.conflict ? 'var(--color-status-red-bg)' : 'var(--color-status-amber-bg, var(--color-bg))',
    border: `1px solid ${pending.conflict ? 'var(--color-status-red)' : 'var(--color-status-amber, var(--color-border))'}`,
    fontSize: 'var(--fs-xs)', color: 'var(--color-ink-2)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)',
  };
  const caution = (s: { confidence: number | null }) => (s.confidence != null && s.confidence < LOW_CONFIDENCE ? ' (low confidence, check the scan)' : '');
  if (pending.conflict) {
    return (
      <div style={box} role="group" aria-label={`Conflicting suggestions for ${field}`}>
        <span style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-status-red)' }}>Documents disagree. Choose one:</span>
        {pending.choices.map((c) => {
          const src = bestSource(c);
          return (
            <div key={c.value} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{c.value}</span>
              <span>from {c.sources.map((x) => x.documentName).join(', ')}{caution(src)}</span>
              <Button variant="secondary" size="sm" onClick={() => onAccept(field, c.value, src.documentId)}>Use this</Button>
            </div>
          );
        })}
        <div><Button variant="ghost" size="sm" onClick={() => onDismiss(field)}>Dismiss both</Button></div>
      </div>
    );
  }
  const choice = pending.choices[0];
  const src = bestSource(choice);
  return (
    <div style={box} role="group" aria-label={`Suggestion for ${field}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{choice.value}</span>
        <span>from {src.documentName}{caution(src)}</span>
        <Button variant="primary" size="sm" onClick={() => onAccept(field, choice.value, src.documentId)}>Accept</Button>
        <Button variant="ghost" size="sm" onClick={() => onDismiss(field)}>Dismiss</Button>
      </div>
    </div>
  );
}

/** "Accept all" for a section: every pending, non-conflicting suggestion among the section's fields. */
function SuggestionToolbar({ fields, pending, onAcceptAll }: {
  fields: WizardField[]; pending: (field: WizardField) => FieldSuggestion | null; onAcceptAll: (fields: WizardField[]) => void;
}) {
  const open = fields.filter((f) => { const p = pending(f); return p && !p.conflict; });
  const conflicts = fields.filter((f) => pending(f)?.conflict);
  if (!open.length && !conflicts.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
      <span>{open.length} suggestion{open.length === 1 ? '' : 's'} in this section{conflicts.length ? `, ${conflicts.length} to settle` : ''}.</span>
      {open.length > 0 && <Button variant="secondary" size="sm" onClick={() => onAcceptAll(fields)}>Accept all</Button>}
    </div>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
      gap: 'var(--sp-4)',
    }}>{children}</div>
  );
}

function Field({ label, required, fullWidth, error, children }: { label: string; required?: boolean; fullWidth?: boolean; error?: string; children: ReactNode }) {
  return (
    <label style={{
      display: 'block',
      gridColumn: fullWidth ? '1 / -1' : undefined,
    }}>
      <span style={{
        display: 'block',
        marginBottom: 'var(--sp-1)',
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)',
        color: 'var(--color-ink)',
      }}>
        {label}
        {required && (
          <span aria-hidden="true" style={{
            color: 'var(--color-status-red)',
            marginLeft: 2,
          }}>*</span>
        )}
      </span>
      {children}
      {error && (
        <span role="alert" style={{ display: 'block', marginTop: 'var(--sp-1)', fontSize: 'var(--fs-sm)', color: 'var(--color-status-red)' }}>{error}</span>
      )}
    </label>
  );
}
